import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  CheckCircle,
  Mail,
  Phone,
  Upload,
} from "lucide-react";
import {
  BUSINESS_TYPES,
  DOCUMENT_TYPES,
  EMPLOYEE_COUNT_RANGES,
  NIGERIA_STATES,
  type DocumentTypeValue,
} from "../../constants/onboarding";
import { submitEmployerOnboarding } from "../../services/api/onboardingService";
import { toUserFacingError } from "../../utils/userFacingError";

interface DocumentSlot {
  type: DocumentTypeValue;
  file: File | null;
}

interface FormState {
  businessName: string;
  contactPersonName: string;
  email: string;
  phone: string;
  businessType: string;
  employeeCountRange: string;
  locationState: string;
  locationCity: string;
}

interface FormErrors {
  businessName?: string;
  contactPersonName?: string;
  email?: string;
  phone?: string;
  businessType?: string;
  employeeCountRange?: string;
  locationState?: string;
  locationCity?: string;
  documents?: string;
}

const defaultDocType: DocumentTypeValue =
  DOCUMENT_TYPES[0]?.value ?? "CAC_CERTIFICATE";

const emptyForm: FormState = {
  businessName: "",
  contactPersonName: "",
  email: "",
  phone: "",
  businessType: "",
  employeeCountRange: "",
  locationState: "",
  locationCity: "",
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  if (digits.startsWith("234") && digits.length === 13) {
    return true;
  }
  if (digits.startsWith("0") && digits.length === 11) {
    return true;
  }
  return digits.length >= 10 && digits.length <= 14;
}

const inputClass =
  "appearance-none block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

const OnboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [slots, setSlots] = useState<DocumentSlot[]>([
    { type: defaultDocType, file: null },
  ]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const visibleSlotCount = (() => {
    let count = 1;
    for (let index = 0; index < slots.length && index < 3; index += 1) {
      const current = slots[index];
      if (current && current.file) {
        count = index + 2;
      }
    }
    return Math.min(count, 4);
  })();

  const updateSlotType = (index: number, type: DocumentTypeValue) => {
    setSlots((prev) => {
      const next = [...prev];
      const current = next[index];
      if (!current) {
        return prev;
      }
      next[index] = { ...current, type };
      return next;
    });
  };

  const updateSlotFile = (index: number, file: File | null) => {
    if (file && file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        documents: "Each document must be 5MB or smaller.",
      }));
      return;
    }

    const currentTotal = slots.reduce(
      (sum, slot) => sum + (slot.file ? slot.file.size : 0),
      0
    );
    const previousSize = slots[index]?.file?.size || 0;
    const nextTotal = currentTotal - previousSize + (file?.size || 0);
    if (nextTotal > 20 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        documents: "Total upload size must stay under 20MB.",
      }));
      return;
    }

    setSlots((prev) => {
      const next = [...prev];
      const current = next[index] ?? { type: defaultDocType, file: null };
      next[index] = { ...current, file };
      if (file && next.length < 4 && index === next.length - 1) {
        next.push({ type: defaultDocType, file: null });
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.documents;
      return next;
    });
  };

  const validate = (): FormErrors => {
    const nextErrors: FormErrors = {};
    if (!form.businessName.trim()) {
      nextErrors.businessName = "Business name is required";
    }
    if (!form.contactPersonName.trim()) {
      nextErrors.contactPersonName = "Contact person name is required";
    }
    if (!form.email.trim() || !isValidEmail(form.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }
    if (!form.phone.trim() || !isValidPhone(form.phone.trim())) {
      nextErrors.phone = "Enter a valid phone number";
    }
    if (!form.businessType) {
      nextErrors.businessType = "Select a business type";
    }
    if (!form.employeeCountRange) {
      nextErrors.employeeCountRange = "Select an employee count range";
    }
    if (!form.locationState) {
      nextErrors.locationState = "Select a state";
    }
    if (!form.locationCity.trim()) {
      nextErrors.locationCity = "City is required";
    }

    const uploaded = slots.filter((slot) => slot.file !== null);
    if (uploaded.length < 1) {
      nextErrors.documents = "Upload at least one business document";
    }
    if (uploaded.length > 4) {
      nextErrors.documents = "You can upload a maximum of 4 documents";
    }

    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const payload = new FormData();
    payload.append("businessName", form.businessName.trim());
    payload.append("contactPersonName", form.contactPersonName.trim());
    payload.append("email", form.email.trim());
    payload.append("phone", form.phone.trim());
    payload.append("businessType", form.businessType);
    payload.append("employeeCountRange", form.employeeCountRange);
    payload.append("locationState", form.locationState);
    payload.append("locationCity", form.locationCity.trim());

    slots.forEach((slot) => {
      if (slot.file) {
        payload.append("documents", slot.file);
        payload.append("documentTypes", slot.type);
      }
    });

    setIsSubmitting(true);
    try {
      await submitEmployerOnboarding(payload);
      navigate("/onboard/submitted");
    } catch (error) {
      setSubmitError(
        toUserFacingError(
          error,
          "Unable to submit your application right now. Please try again later."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden lg:flex lg:w-1/2 relative" style={{ backgroundColor: "#0076b5" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-blue-700 opacity-90" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-2">Register your business</h1>
            <p className="text-blue-100">
              Submit your company details and documents. We will review your
              application before activating your employer portal.
            </p>
          </div>
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">One form, no password yet</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">CAC and related documents</span>
            </div>
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-300" />
              <span className="text-blue-100">Admin review before access</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 sm:px-6 lg:px-8 py-10 overflow-y-auto">
        <div className="max-w-xl w-full space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Business onboarding</h2>
            <p className="mt-2 text-sm text-gray-600">
              Already approved?{" "}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Go to login
              </Link>
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <p className="ml-3 text-sm text-red-800">{submitError}</p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">Business name</label>
              <input
                className={inputClass}
                value={form.businessName}
                onChange={(event) => updateField("businessName", event.target.value)}
                placeholder="e.g. Acme Limited"
              />
              {errors.businessName && (
                <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact person full name
              </label>
              <input
                className={inputClass}
                value={form.contactPersonName}
                onChange={(event) => updateField("contactPersonName", event.target.value)}
                placeholder="e.g. Jane Doe"
              />
              {errors.contactPersonName && (
                <p className="mt-1 text-sm text-red-600">{errors.contactPersonName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <div className="relative">
                <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                <input
                  type="email"
                  className={`${inputClass} pl-10`}
                  value={form.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  placeholder="you@company.com"
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Phone number</label>
              <div className="relative">
                <Phone className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                <input
                  className={`${inputClass} pl-10`}
                  value={form.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  placeholder="08012345678"
                />
              </div>
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Business type</label>
              <select
                className={inputClass}
                value={form.businessType}
                onChange={(event) => updateField("businessType", event.target.value)}
              >
                <option value="">Select business type</option>
                {BUSINESS_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.businessType && (
                <p className="mt-1 text-sm text-red-600">{errors.businessType}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Employee count range
              </label>
              <select
                className={inputClass}
                value={form.employeeCountRange}
                onChange={(event) => updateField("employeeCountRange", event.target.value)}
              >
                <option value="">Select range</option>
                {EMPLOYEE_COUNT_RANGES.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
              {errors.employeeCountRange && (
                <p className="mt-1 text-sm text-red-600">{errors.employeeCountRange}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">State</label>
                <select
                  className={inputClass}
                  value={form.locationState}
                  onChange={(event) => updateField("locationState", event.target.value)}
                >
                  <option value="">Select state</option>
                  {NIGERIA_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
                {errors.locationState && (
                  <p className="mt-1 text-sm text-red-600">{errors.locationState}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  className={inputClass}
                  value={form.locationCity}
                  onChange={(event) => updateField("locationCity", event.target.value)}
                  placeholder="e.g. Ikeja"
                />
                {errors.locationCity && (
                  <p className="mt-1 text-sm text-red-600">{errors.locationCity}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business documents (up to 4)
              </label>
              <div className="space-y-3">
                {slots.slice(0, visibleSlotCount).map((slot, index) => (
                  <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <select
                      className={inputClass}
                      value={slot.type}
                      onChange={(event) =>
                        updateSlotType(index, event.target.value as DocumentTypeValue)
                      }
                    >
                      {DOCUMENT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center justify-center px-3 py-3 border border-dashed border-gray-300 rounded-lg cursor-pointer text-sm text-gray-600 hover:bg-gray-50">
                      <Upload className="h-4 w-4 mr-2" />
                      {slot.file ? slot.file.name : "Upload PDF, JPG, or PNG"}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={(event) => {
                          const fileList = event.target.files;
                          const file = fileList && fileList.length > 0 ? fileList[0] : null;
                          updateSlotFile(index, file ?? null);
                        }}
                      />
                    </label>
                  </div>
                ))}
              </div>
              {errors.documents && (
                <p className="mt-1 text-sm text-red-600">{errors.documents}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: "#0076b5" }}
            >
              {isSubmitting ? "Submitting..." : "Submit application"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardPage;
